import React, { useEffect, useCallback } from "react";
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useForm from "../../../hooks/useForm";
import useLoader from "../../../hooks/useLoader";
import { editMovementsFields } from "../../../constants/forms/movementFields";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import { formatDateToInput } from "../../../utils/formatDateToInput";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import useNotification from "../../../hooks/useNotification";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditMovementPage = () => {
  const navigate = useNavigate();
  const { movementId } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    getByColumn: baseServiceGetById,
    put: baseServiceUpdate,
    formErrors,
  } = useBaseService(navigate);
  const { formData, handleChange, formatData, setFormData } = useForm(
    setDefaultFieldValues(editMovementsFields),
    editMovementsFields,
  );
  const queryClient = useQueryClient();

  const {
    data: movementData,
    isLoading,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ["movements", movementId],
    queryFn: async () => {
      const response = await baseServiceGetById(
        entities.movements.getById(movementId),
      );
      return response.result;
    },
  });

  useEffect(() => {
    if (!isLoading && movementData) {
      formatData(movementData, editMovementsFields);
      setFormData((prev) => ({
        ...prev,
        movement_date: formatDateToInput(movementData.movement_date),
      }));
    }
    if (isError && error) {
      console.log("Erro ao buscar movimento", error);
      showNotification("error", `Erro ao buscar movimento: ${error?.message}`);
    }
  }, [
    error,
    formatData,
    isError,
    isLoading,
    movementData,
    setFormData,
    showNotification,
  ]);

  const updateMovement = useMutation({
    mutationFn: async (formData) => {
      showLoader();
      const response = await baseServiceUpdate(
        entities.movements.update(movementId),
        formData,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        navigate("/aluguel/movimentos");
        queryClient.invalidateQueries({ queryKey: ["movements"] });
      }
      hideLoader();
    },
    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const handleSubmit = async () => {
    try {
      await updateMovement.mutateAsync(formData);
    } catch (error) {
      console.error("Erro ao enviar movimento:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleBack = useCallback(() => {
    navigate("/aluguel/movimentos");
  }, [navigate]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Editar Movimentos #${movementId}`}
        showBackButton={true}
        backUrl="/aluguel/movimentos"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Atualizar"
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}>
          {() => {
            const renderedSections = new Set();

            return editMovementsFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditMovementPage;
